import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import {
  type OpenPositionAggregateSearchBody,
  type OpenPositionAggregateSearchParameters,
  useOpenPositionCustomerAggregateSearchQuery,
} from "@/common/api/open-position/use-open-position-customer-aggregate-search.query";
import { createBasePredefinedTable } from "@/components/data-table/predefined/_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  BaseSorting,
  DataFetchHooks,
  PredefinedTableConfig,
} from "@/components/data-table/predefined/_components/base-predefined-table.types";
import { TableFeatures } from "@/components/data-table/types";
import { BranchFilter } from "@/components/filter/predefined/branch-filter";
import { CompanyFilter } from "@/components/filter/predefined/company-filter";
import { CompanyGroupFilter } from "@/components/filter/predefined/company-group-filter";
import i18next from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useOpenPositionSearchBody } from "../_hooks/use-open-position-search-body";
import { AggregateSearchHookArgs, useAggregateTableQuery } from "./_hooks/use-aggregate-table-query";
import { CustomerAggregateRow, customerAggregateColumns, toCustomerAggregateRow } from "./customer-aggregate-columns";

type CustomerAggregateSorting = BaseSorting;

function useCustomerAggregateTableQuery(args?: AggregateSearchHookArgs) {
  return useAggregateTableQuery(useOpenPositionCustomerAggregateSearchQuery, toCustomerAggregateRow, args);
}

const DEFAULT_COLUMNS = [
  customerAggregateColumns.name,
  customerAggregateColumns.invoiceBalance,
  customerAggregateColumns.paymentBalance,
  customerAggregateColumns.totalBalance,
  customerAggregateColumns.positionCount,
  customerAggregateColumns.overdueAmount,
];

const TABLE_CONFIG: PredefinedTableConfig<CustomerAggregateSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [{ field: "totalBalance", direction: "desc" }],
  defaultTableConfig: {
    searchPlaceholder: i18next.t("fia:SearchCustomers"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  },
  defaultRowActions: {
    showEdit: false,
    showView: false,
    showDelete: false,
    showRestore: false,
  },
  sortingMapping: {
    name: ["name"],
    invoiceBalance: ["invoiceBalance"],
    paymentBalance: ["paymentBalance"],
    totalBalance: ["totalBalance"],
    positionCount: ["positionCount"],
    overdueAmount: ["overdueAmount"],
  },
};

const QUERY_HOOKS: DataFetchHooks<
  CustomerAggregateRow,
  OpenPositionAggregateSearchParameters,
  OpenPositionAggregateSearchBody
> = {
  useSearchQuery: useCustomerAggregateTableQuery,
};

const BaseCustomerAggregateTable = createBasePredefinedTable({
  config: TABLE_CONFIG,
  queryHooks: QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function CustomerAggregateTable(
  props: BasePredefinedTableProps<
    CustomerAggregateRow,
    CustomerAggregateSorting,
    OpenPositionAggregateSearchParameters,
    OpenPositionAggregateSearchBody
  >,
) {
  const { t } = useTranslation(["fia"]);
  const { name = "customer-aggregate-table", filters, body, ...restProps } = props;

  const bodyWithReferenceDate = useOpenPositionSearchBody(body);

  const defaultFilters = useMemo(
    () =>
      filters ?? [
        new BranchFilter({ id: "branch" }),
        new CompanyGroupFilter({ id: "companyGroup" }),
        new CompanyFilter({ id: "company" }),
      ],
    [filters],
  );

  return (
    <BaseCustomerAggregateTable
      name={name}
      searchPlaceholder={t("fia:SearchCustomers")}
      filters={defaultFilters}
      body={bodyWithReferenceDate}
      onRowNavigate={(row) => `~/masterdata/customer/${row.customer.uuid}`}
      {...restProps}
    />
  );
}
